import React, { Component } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';

import { withAccount } from '../../components/AccountContext';
import { withGrowl } from '../../components/GrowlNotifications';
import UserInvitationList from './UserInvitationList';
import { parse, filterPaginationWithSize, emptyConnection } from '../../utils/UrlUtil';
import { growlShape } from '../../components/prop-types';

export const ALL_INVITATIONS = gql`
  query getAllJoinAccountInvitations(
    $accountId: ID!,
    $after: String,
    $before: String,
    $first: Int,
    $last: Int,
    $filter: JoinAccountInvitationFilter
    $sort: JoinAccountInvitationSortSpecifier
  ) {
    account(id: $accountId) {
      id
      pendingJoinAccountInvitations(
        after: $after,
        before: $before,
        first: $first,
        last: $last,
        filter: $filter,
        sort: $sort
      ) {
        indexFrom
        indexTo
        totalCount
        pageInfo {
          endCursor
          startCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          node {
            id
            email
            fullName
            role {
              id
              name
              displayName
              description
            }
            createdOn
            invitedOn
          }
        }
      }
    }
  }
`;

const SEND_INVITATION = gql`
  mutation sendJoinAccountInvitation($input: SendJoinAccountInvitationInput!) {
    sendJoinAccountInvitation(input: $input) {
      clientMutationId
    }
  }
`;

const CANCEL_INVITATION = gql`
  mutation cancelJoinAccountInvitation($input: CancelJoinAccountInvitationInput!) {
    cancelJoinAccountInvitation(input: $input) {
      clientMutationId
    }
  }
`;

// https://codesandbox.io/s/apollo-and-react-hooks-4vril
// https://github.com/apollographql/react-apollo/tree/master/packages/hooks
function UserInvitationsContainer({ children, variables }) {
  const { loading, error, data } = useQuery(ALL_INVITATIONS, { variables, fetchPolicy: 'network-only' });
  const [sendInvitationMutation, sendInvitationResult] = useMutation(SEND_INVITATION);
  const [canceleMutation, cancelResult] = useMutation(CANCEL_INVITATION, {
    refetchQueries: ['getAllJoinAccountInvitations'],
  });

  return children({
    invitation: { loading, data, error },
    sendInvitation: { mutation: sendInvitationMutation, result: sendInvitationResult },
    cancelInvitation: { mutation: canceleMutation, result: cancelResult },
  });
}

class UserInvitationsContainerWrapper extends Component {
  render() {
    const { currentAccount, location, growl } = this.props;
    const params = parse(location.search);
    const pagination = filterPaginationWithSize(params);

    const variables = {
      ...pagination,
      accountId: currentAccount,
      sort: params.sort,
      filter: params.filter,
    };
    return (
      <UserInvitationsContainer variables={variables}>
        {({ invitation: { loading, error, data }, sendInvitation, cancelInvitation }) => {
          if (error) {
            growl.show({
              sticky: true,
              severity: 'error',
              detail: error.message,
            });
          }

          const connection = data && data.account ? data.account.pendingJoinAccountInvitations : emptyConnection();

          return (
            <UserInvitationList
              connection={connection}
              currentAccount={currentAccount}
              sendInvitation={sendInvitation}
              cancelInvitation={cancelInvitation}
              loading={loading}
            />
          );
        }}
      </UserInvitationsContainer>
    );
  }
}

UserInvitationsContainerWrapper.propTypes = {
  location: PropTypes.objectOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
};

export default withRouter(injectIntl(withAccount(withGrowl(UserInvitationsContainerWrapper))));
